import { useEffect, useRef, useState } from "react";

/**
 * Milestone meter — a slim horizontal progress bar that fills up as more
 * viewers join, with each of the 5 tier thresholds rendered as a tick and
 * price stub above the bar.
 *
 * Neon-lime track outline, orange gradient fill, `data-testid=milestone-meter`.
 * Sits below the chat input in the mobile LiveRoom overlay so hosts + members
 * see the crash progress at a glance.
 *
 * ### Milestone-hit pop
 * When a NEW tier is unlocked (i.e. the winning `activeIndex` bumps up from
 * the last render), we fire an ephemeral centre-video popup ("Milestone
 * unlocked · ₹1,614") via the `onMilestoneHit` callback. Parent controls the
 * popup so it can live in a different DOM layer.
 */
export default function MilestoneMeter({ milestones = [], participants = 0, onMilestoneHit, compact = false }) {
  const tiers = Array.isArray(milestones) ? milestones : [];
  const lastThreshold = tiers.length ? (tiers[tiers.length - 1].threshold ?? tiers[tiers.length - 1].target ?? 1) : 1;

  // Fill % based on participants / last tier threshold (capped at 100%).
  const pct = Math.max(0, Math.min(100, (participants / Math.max(lastThreshold, 1)) * 100));

  // Which tier is currently "unlocked" (highest threshold met). Null if none.
  let activeIdx = -1;
  tiers.forEach((m, i) => {
    if (participants >= (m.threshold ?? m.target ?? 0)) activeIdx = i;
  });

  // Fire onMilestoneHit whenever activeIdx strictly increments.
  const lastActiveRef = useRef(activeIdx);
  useEffect(() => {
    if (activeIdx > lastActiveRef.current && activeIdx >= 0 && onMilestoneHit) {
      onMilestoneHit(tiers[activeIdx], activeIdx);
    }
    lastActiveRef.current = activeIdx;
  }, [activeIdx, tiers, onMilestoneHit]);

  if (tiers.length === 0) return null;

  return (
    <div
      className={`select-none w-full ${compact ? "px-3 py-2" : "px-3.5 py-2.5"} bg-black/45 backdrop-blur border-t border-[#B9E641]/40`}
      data-testid="milestone-meter"
    >
      {/* Header line: label + current live price */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#B9E641]">
          ⚡ Price Crash · <span className="tabular-nums">{participants}</span> in
        </div>
        {activeIdx >= 0 && (
          <div className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-white/70">
            Tier {activeIdx + 1} · ₹{Number(tiers[activeIdx].price).toLocaleString("en-IN")}
          </div>
        )}
      </div>

      {/* Bar */}
      <div className="relative h-2 border border-[#B9E641] bg-black/40 shadow-[0_0_6px_rgba(185,230,65,0.35)]">
        {/* Orange fill with soft neon glow */}
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FF6A00] to-[#FF3B30] transition-[width] duration-500 ease-out shadow-[0_0_8px_rgba(255,106,0,0.6)]"
          style={{ width: `${pct}%` }}
          data-testid="milestone-meter-fill"
        />
        {/* Threshold ticks */}
        {tiers.map((m, i) => {
          const t = (m.threshold ?? m.target ?? 0);
          const left = Math.min(100, (t / Math.max(lastThreshold, 1)) * 100);
          const hit = i <= activeIdx;
          return (
            <div
              key={i}
              className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1 h-3.5 ${hit ? "bg-[#FFFFFF]" : "bg-[#B9E641]"} pointer-events-none`}
              style={{ left: `${left}%` }}
              data-testid={`milestone-meter-tick-${i + 1}`}
              title={`≥${t} · ₹${m.price}`}
            />
          );
        })}
      </div>

      {/* Sub-tick prices (compact row of 5 tier prices under the bar) */}
      <div className="mt-1.5 flex items-start justify-between">
        {tiers.map((m, i) => {
          const t = m.threshold ?? m.target ?? 0;
          const hit = i <= activeIdx;
          return (
            <div
              key={i}
              className={`flex flex-col items-center leading-none font-mono-ds text-[9px] tabular-nums ${hit ? "text-[#B9E641]" : "text-white/50"}`}
              style={{ width: `${100 / tiers.length}%` }}
            >
              <span className="font-bold">₹{Number(m.price).toLocaleString("en-IN")}</span>
              <span className="opacity-70 mt-0.5">≥{t}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Play a tiny two-note "coin drop" sting via WebAudio.
 * Exported so callers can fire the sensory nudge on the parent side (once
 * per hit) instead of inside the popup component (which mounts twice in the
 * mobile+desktop DOM layers).
 */
export function playCoinSting() {
  try {
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return;
    const ctx = new AudioCtor();
    const now = ctx.currentTime;
    const play = (freq, start, dur, gain = 0.14) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(gain, now + start + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };
    play(880, 0.00, 0.09);   // A5
    play(1318.5, 0.07, 0.13); // E6 (ka-ching)
    // Close the ctx after playback so we don't leak on rapid taps.
    setTimeout(() => { try { ctx.close(); } catch (_e) { /* noop */ } }, 400);
  } catch (_e) { /* noop */ }
}

/** Fire a short haptic buzz if the device supports it and the user hasn't opted out of motion. */
export function vibrateMilestone() {
  try {
    if (typeof window === "undefined" || !window.navigator?.vibrate) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    window.navigator.vibrate([15, 40, 15]);
  } catch (_e) { /* noop */ }
}

/**
 * Centre-video pop-up rendered on every milestone unlock.
 * Auto-dismisses via a 2.5s CSS animation (`.animate-milestone-pop`). Parent
 * mounts once and re-mounts with a fresh `key` on each hit so the animation
 * plays from the top. On mount we ALSO fire a tiny sensory nudge — 200ms
 * "coin drop" audio sting + 70ms haptic buzz — to make the price crash feel
 * dopamine-heavy on mobile. Both nudges silently no-op if the platform
 * doesn't support them or the user prefers reduced motion.
 */
/**
 * Centre-video pop-up rendered on every milestone unlock.
 * Auto-dismisses via a 2.5s CSS animation (`.animate-milestone-pop`). Parent
 * mounts once and re-mounts with a fresh `key` on each hit so the animation
 * plays from the top. Note: sensory sting (audio + haptic) is fired by the
 * parent's dedupe callback, NOT by this component, because it's mounted in
 * both mobile + desktop DOM layers and would otherwise play twice per hit.
 */
export function MilestoneHitPopup({ tier, index, onDone }) {
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => {
    const t = setTimeout(() => { if (onDoneRef.current) onDoneRef.current(); }, 2500);
    return () => clearTimeout(t);
  }, []);
  if (!tier) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-[38%] flex justify-center z-40"
      data-testid="milestone-hit-popup"
    >
      <div className="animate-milestone-pop bg-[#0F0F0F]/90 backdrop-blur border-2 border-[#FF6A00] shadow-[0_0_30px_rgba(255,106,0,0.6)] px-5 py-4 text-center max-w-[90%]">
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.28em] text-[#B9E641] font-bold">
          ⚡ Milestone {(index ?? 0) + 1} Unlocked
        </div>
        <div className="mt-1.5 font-display text-4xl md:text-5xl uppercase tracking-tight leading-none text-white tabular-nums">
          ₹{Number(tier.price).toLocaleString("en-IN")}
        </div>
        <div className="mt-1.5 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-white/70">
          Everyone locked in at this price if timer ends now
        </div>
      </div>
    </div>
  );
}
